import { GrandImage } from 'ui'
import { Box } from '@chakra-ui/react'
import { type PaymentMethodLogosConfig, type PaymentProviderName } from 'ecosystem'
import { PaymentMethodLogos } from '../../PaymentMethodLogos'
import { useFooterContext } from '../FooterContext'
import { resolvePaymentStrip } from '../../PaymentMethodLogos'

interface FooterPaymentLogoProps {
  paymentProviderName?: PaymentProviderName
  /** Merchant-curated method marks. Takes precedence over the provider badge. */
  methodLogos?: PaymentMethodLogosConfig
}

/**
 * Payment reassurance in the footer.
 *
 * Prefers the merchant's own selection of payment-*method* marks. The per-provider badge
 * below is the fallback for stores that have not configured any — it stays so existing
 * storefronts look exactly as they do today until someone opts in.
 */
export const FooterPaymentLogo = ({ paymentProviderName, methodLogos }: FooterPaymentLogoProps) => {
  const { contrastColorTheme } = useFooterContext()

  const footer = methodLogos?.footer

  if (footer?.methods?.length || footer?.combinedImageUrl || footer?.combinedPreset) {
    return <PaymentMethodLogos config={footer} contrast={contrastColorTheme} />
  }

  switch (paymentProviderName ?? 'KLARNA') {
    case 'KLARNA':
      return (
        <GrandImage
          alt="Klarna"
          src={resolvePaymentStrip('klarna', contrastColorTheme === 'light')!}
        />
      )
    case 'NEXI':
      return (
        <GrandImage
          alt="Nexi payment methods"
          src={resolvePaymentStrip('nexi', contrastColorTheme === 'light')!}
          maxW="min(600px, 100%)"
          sizes="(min-width: 600px) 600px, 100vw"
        />
      )
    case 'SVEA':
      return (
        <Box
          width={{ base: 'full', md: '500px' }}
          height={{ base: '100px', md: '200px' }}
          position="relative"
        >
          <GrandImage
            alt="Svea payment methods"
            src={resolvePaymentStrip('svea', contrastColorTheme === 'light')!}
            fill
            objectFit="contain"
          />
        </Box>
      )
    case 'QLIRO':
      return (
        <GrandImage
          alt="Qliro"
          maxW="min(600px, 100%)"
          sizes="(min-width: 600px) 600px, 100vw"
          src={resolvePaymentStrip('qliro', contrastColorTheme === 'light')!}
        />
      )
    // Briqpay is an orchestrator, so the badge should advertise the PSPs actually enabled
    // for the store rather than Briqpay itself — and that set lives in the merchant
    // portal, not in our config. Render nothing until we have on-site material for it.
    case 'BRIQPAY':
      return null
    // todo: add LEDYER logo
    case 'LEDYER':
      return <>LEDYER img</>
  }
}
